<template>
  <div class="min-h-screen lg:flex">
    <Sidebar />
    <GlobalNavbar />
    <main class="min-w-0 flex-1 px-4 pb-8 pt-24 lg:ml-64 lg:px-8">
      <div>
        <p class="text-sm font-semibold text-blue-600">Geradores</p>
        <h1 class="mt-3 text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">Gerador de stack</h1>
        <p class="mt-4 max-w-3xl text-lg leading-8 text-zinc-600">Preencha os dados da sua instalação e gere um compose pronto para copiar ou baixar.</p>

        <div class="mt-10 w-full">
          <div class="relative mb-8 grid grid-cols-3 before:absolute before:left-4 before:right-4 before:top-4 before:h-px before:bg-zinc-200 before:content-['']" aria-label="Etapas do gerador">
            <div v-for="item in steps" :key="item.number" :class="item.number === 1 ? 'justify-self-start' : item.number === 2 ? 'justify-self-center' : 'justify-self-end'" class="relative z-10 flex items-center gap-3 bg-white px-2">
              <div :class="step >= item.number ? 'bg-zinc-950 text-white' : 'bg-zinc-100 text-zinc-500'" class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                {{ item.number }}
              </div>
              <span :class="step >= item.number ? 'text-zinc-950' : 'text-zinc-500'" class="truncate text-sm font-medium">{{ item.label }}</span>
            </div>
          </div>

          <section v-if="step === 1" class="rounded-xl border border-zinc-200 bg-white p-6 sm:p-8">
            <p class="text-sm font-semibold text-blue-600">Etapa 1 de 3</p>
            <h2 class="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">Escolha a solução</h2>
            <p class="mt-2 text-sm leading-6 text-zinc-600">Comece selecionando qual stack você deseja configurar.</p>
            <div class="mt-6 grid gap-3 sm:grid-cols-2">
              <button v-for="option in products" :key="option.id" type="button" :class="['rounded-lg border px-4 py-3 text-left transition', product === option.id ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-200 text-zinc-700 hover:border-zinc-400']" @click="product = option.id">
                <span class="block text-sm font-semibold">{{ option.name }}</span>
                <span :class="product === option.id ? 'text-zinc-300' : 'text-zinc-500'" class="mt-1 block text-xs">{{ option.description }}</span>
              </button>
            </div>
            <div class="mt-8 flex justify-end border-t border-zinc-100 pt-6">
              <button type="button" class="inline-flex items-center gap-2 rounded-md bg-zinc-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-800" @click="step = 2">
                Continuar <PhArrowRight class="h-4 w-4" />
              </button>
            </div>
          </section>

          <section v-else-if="step === 2" class="rounded-xl border border-zinc-200 bg-white p-6 sm:p-8">
            <p class="text-sm font-semibold text-blue-600">Etapa 2 de 3</p>
            <h2 class="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">Dados da instalação</h2>
            <p class="mt-2 text-sm leading-6 text-zinc-600">Preencha os dados necessários para montar sua configuração.</p>
            <div class="mt-6 grid gap-4 sm:grid-cols-2">
              <label v-for="field in fields" :key="field.key" class="block text-sm font-medium text-zinc-700">
                {{ field.label }}
                <input v-model="values[field.key]" :type="field.secret ? 'password' : 'text'" :placeholder="field.placeholder" class="mt-1.5 w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm text-zinc-950 outline-none focus:border-zinc-500 focus:ring-2 focus:ring-zinc-200" />
              </label>
            </div>
            <div class="mt-8 flex flex-col-reverse gap-3 border-t border-zinc-100 pt-6 sm:flex-row sm:justify-between">
              <button type="button" class="inline-flex items-center gap-2 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-500" @click="step = 1">
                <PhArrowLeft class="h-4 w-4" /> Voltar
              </button>
              <button type="button" class="inline-flex items-center gap-2 rounded-md bg-zinc-950 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-800" @click="step = 3">
                Revisar compose <PhArrowRight class="h-4 w-4" />
              </button>
            </div>
          </section>

          <section v-else class="min-w-0 rounded-xl border border-zinc-200 bg-white p-6 sm:p-8">
            <p class="text-sm font-semibold text-blue-600">Etapa 3 de 3</p>
            <div class="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 class="text-2xl font-semibold tracking-tight text-zinc-950">Revise e baixe</h2>
                <p class="mt-2 text-sm leading-6 text-zinc-600">Confira o compose gerado antes de fazer o deploy no painel.</p>
              </div>
              <div class="flex flex-wrap gap-2">
                <button type="button" class="inline-flex items-center gap-2 rounded-md border border-zinc-300 px-3 py-2 text-sm font-medium text-zinc-700 hover:border-zinc-500" @click="copyCompose">
                  <PhCheck v-if="copied" class="h-4 w-4 text-emerald-600" weight="bold" />
                  <PhCopy v-else class="h-4 w-4" weight="duotone" />
                  {{ copied ? 'Copiado' : 'Copiar' }}
                </button>
                <button type="button" class="inline-flex items-center gap-2 rounded-md bg-zinc-950 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800" @click="downloadCompose">
                  <PhDownloadSimple class="h-4 w-4" /> Baixar
                </button>
              </div>
            </div>
            <pre class="mt-4 max-h-[70vh] overflow-auto rounded-xl bg-zinc-950 p-5 text-xs leading-5 text-zinc-200"><code>{{ compose }}</code></pre>
            <div class="mt-6 flex justify-start border-t border-zinc-100 pt-6">
              <button type="button" class="inline-flex items-center gap-2 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-medium text-zinc-700 hover:border-zinc-500" @click="step = 2">
                <PhArrowLeft class="h-4 w-4" /> Editar dados
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { PhArrowLeft, PhArrowRight, PhCheck, PhCopy, PhDownloadSimple } from "@phosphor-icons/vue";
import Sidebar from "../components/layout/Sidebar.vue";
import GlobalNavbar from "../components/layout/GlobalNavbar.vue";

type Product = "kanban" | "stackzap";
const product = ref<Product>("kanban");
const step = ref(1);
const copied = ref(false);
const steps = [
  { number: 1, label: "Solução" },
  { number: 2, label: "Dados" },
  { number: 3, label: "Revisão" },
];
const values = reactive<Record<string, string>>({ domain: "", version: "latest", postgresHost: "postgres", postgresUser: "postgres", postgresPassword: "", postgresDatabase: "chatwoot", redisHost: "redis", stacklabToken: "", secretKey: "", apiKey: "", encryptionKey: "" });
const products = [
  { id: "kanban" as Product, name: "Kanban para Chatwoot", description: "Gere a stack do Chatwoot com Kanban e Sidekiq." },
  { id: "stackzap" as Product, name: "StackZap", description: "Gere a stack da API StackZap com dados persistentes." },
];
const fields = computed(() => product.value === "kanban" ? [
  { key: "domain", label: "Domínio do Chatwoot", placeholder: "app.exemplo.com" },
  { key: "version", label: "Versão da imagem", placeholder: "latest" },
  { key: "postgresHost", label: "Host do PostgreSQL", placeholder: "postgres" },
  { key: "postgresUser", label: "Usuário do PostgreSQL", placeholder: "postgres" },
  { key: "postgresPassword", label: "Senha do PostgreSQL", placeholder: "Sua senha", secret: true },
  { key: "postgresDatabase", label: "Banco do Chatwoot", placeholder: "chatwoot" },
  { key: "redisHost", label: "Host do Redis", placeholder: "redis" },
  { key: "stacklabToken", label: "Token da StackLab", placeholder: "Token recebido na compra", secret: true },
  { key: "secretKey", label: "SECRET_KEY_BASE", placeholder: "Chave secreta do Chatwoot", secret: true },
] : [
  { key: "domain", label: "Domínio da StackZap", placeholder: "api.exemplo.com" },
  { key: "version", label: "Versão da imagem", placeholder: "latest" },
  { key: "postgresHost", label: "Host do PostgreSQL", placeholder: "postgres" },
  { key: "postgresUser", label: "Usuário do PostgreSQL", placeholder: "stackzap" },
  { key: "postgresPassword", label: "Senha do PostgreSQL", placeholder: "Sua senha", secret: true },
  { key: "postgresDatabase", label: "Banco da StackZap", placeholder: "stackzap" },
  { key: "redisHost", label: "Host do Redis", placeholder: "redis" },
  { key: "apiKey", label: "Chave global da API", placeholder: "Chave da API", secret: true },
  { key: "encryptionKey", label: "Chave de criptografia", placeholder: "Chave longa", secret: true },
]);
const value = (key: string, fallback: string) => values[key] || `<${fallback}>`;
const compose = computed(() => product.value === "kanban" ? `version: "3.7"

services:
  chatwoot_app:
    image: stacklabdigital/kanban:${value("version", "VERSAO_DA_IMAGEM")}
    command: bundle exec rails s -p 3000 -b 0.0.0.0
    entrypoint: docker/entrypoints/rails.sh
    volumes:
      - chatwoot_storage:/app/storage
    environment:
      FRONTEND_URL: https://${value("domain", "DOMINIO_DO_CHATWOOT")}
      SECRET_KEY_BASE: ${value("secretKey", "SECRET_KEY_BASE")}
      REDIS_URL: redis://${value("redisHost", "HOST_REDIS")}:6379
      POSTGRES_HOST: ${value("postgresHost", "HOST_POSTGRES")}
      POSTGRES_USERNAME: ${value("postgresUser", "USUARIO_POSTGRES")}
      POSTGRES_PASSWORD: ${value("postgresPassword", "SENHA_POSTGRES")}
      POSTGRES_DATABASE: ${value("postgresDatabase", "BANCO_CHATWOOT")}
      STACKLAB_TOKEN: ${value("stacklabToken", "TOKEN_DA_STACKLAB")}
      RAILS_ENV: production
      INSTALLATION_ENV: docker
      RAILS_LOG_TO_STDOUT: "true"
    networks: [chatwoot]
    deploy:
      mode: replicated
      replicas: 1
      placement:
        constraints:
          - node.role == manager
      resources:
        limits:
          cpus: "1"
          memory: 1024M
      labels:
        - traefik.enable=true
        - traefik.http.routers.chatwoot_app.rule=Host(\`${value("domain", "DOMINIO_DO_CHATWOOT")}\`)
        - traefik.http.routers.chatwoot_app.entrypoints=websecure
        - traefik.http.routers.chatwoot_app.tls.certresolver=<NOME_DO_CERTRESOLVER>
        - traefik.http.routers.chatwoot_app.service=chatwoot_app
        - traefik.http.services.chatwoot_app.loadbalancer.server.port=3000

  chatwoot_sidekiq:
    image: stacklabdigital/kanban:${value("version", "VERSAO_DA_IMAGEM")}
    command: bundle exec sidekiq -C config/sidekiq.yml
    volumes:
      - chatwoot_storage:/app/storage
    environment:
      SECRET_KEY_BASE: ${value("secretKey", "SECRET_KEY_BASE")}
      REDIS_URL: redis://${value("redisHost", "HOST_REDIS")}:6379
      POSTGRES_HOST: ${value("postgresHost", "HOST_POSTGRES")}
      POSTGRES_USERNAME: ${value("postgresUser", "USUARIO_POSTGRES")}
      POSTGRES_PASSWORD: ${value("postgresPassword", "SENHA_POSTGRES")}
      POSTGRES_DATABASE: ${value("postgresDatabase", "BANCO_CHATWOOT")}
      STACKLAB_TOKEN: ${value("stacklabToken", "TOKEN_DA_STACKLAB")}
      RAILS_ENV: production
    networks: [chatwoot]
    deploy:
      mode: replicated
      replicas: 1
      placement:
        constraints:
          - node.role == manager

volumes:
  chatwoot_storage:
    external: true
    name: <NOME_DO_VOLUME_CHATWOOT>

networks:
  chatwoot:
    external: true
    name: <NOME_DA_REDE_INTERNA>` : `version: "3.7"

services:
  stackzap:
    image: stacklabdigital/stackzap:${value("version", "VERSAO_DA_IMAGEM")}
    volumes:
      - stackzap_data:/app/data
      - stackzap_logs:/app/logs
    environment:
      ENV: production
      APP_ENV: production
      DEV_MODE: "false"
      PORT: "8080"
      SERVER_PORT: "8080"
      STACKZAP_PUBLIC_URL: https://${value("domain", "DOMINIO_DA_STACKZAP")}
      GLOBAL_API_KEY: ${value("apiKey", "CHAVE_GLOBAL_DA_API")}
      STACKZAP_ENCRYPTION_KEY: ${value("encryptionKey", "CHAVE_DE_CRIPTOGRAFIA")}
      AUTO_MIGRATE: "true"
      DATABASE_URL: postgresql://${value("postgresUser", "USUARIO_POSTGRES")}:${value("postgresPassword", "SENHA_POSTGRES")}@${value("postgresHost", "HOST_POSTGRES")}:5432/${value("postgresDatabase", "BANCO_STACKZAP")}?sslmode=disable
      REDIS_URL: redis://${value("redisHost", "HOST_REDIS")}:6379/0
      WEBHOOK_WORKER_ENABLED: "true"
      QUEUE_WORKER_ENABLED: "true"
      SCHEDULER_ENABLED: "true"
    networks:
      - <NOME_DA_REDE_INTERNA>
    deploy:
      mode: replicated
      replicas: 1
      placement:
        constraints:
          - node.role == manager
      labels:
        - traefik.enable=true
        - traefik.http.routers.stackzap.rule=Host(\`${value("domain", "DOMINIO_DA_STACKZAP")}\`)
        - traefik.http.routers.stackzap.entrypoints=websecure
        - traefik.http.routers.stackzap.tls.certresolver=<NOME_DO_CERTRESOLVER>
        - traefik.http.routers.stackzap.service=stackzap
        - traefik.http.services.stackzap.loadbalancer.server.port=8080
        - traefik.http.services.stackzap.loadbalancer.passHostHeader=true
volumes:
  stackzap_data:
    external: true
    name: <NOME_DO_VOLUME_DADOS>
  stackzap_logs:
    external: true
    name: <NOME_DO_VOLUME_LOGS>

networks:
  <NOME_DA_REDE_INTERNA>:
    external: true
    name: <NOME_DA_REDE_INTERNA>`);
const copyCompose = async () => { await navigator.clipboard.writeText(compose.value); copied.value = true; window.setTimeout(() => { copied.value = false; }, 2000); };
const downloadCompose = () => { const blob = new Blob([compose.value], { type: "text/yaml" }); const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = `${product.value}-stack.yml`; link.click(); URL.revokeObjectURL(link.href); };
</script>
